import React, { InputHTMLAttributes, forwardRef } from 'react';
import styled from 'styled-components';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  isFullWidth?: boolean;
}

const InputContainer = styled.div<{ $isFullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${props => (props.$isFullWidth ? '100%' : 'auto')};
  margin-bottom: 16px;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #343a40;
  margin-bottom: 4px;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input<{ $hasLeftIcon: boolean; $hasRightIcon: boolean; $hasError: boolean }>`
  padding: 12px 16px;
  padding-left: ${props => (props.$hasLeftIcon ? '40px' : '16px')};
  padding-right: ${props => (props.$hasRightIcon ? '40px' : '16px')};
  font-size: 1rem;
  border-radius: 8px;
  border: 1px solid ${props => (props.$hasError ? '#fa5252' : '#dee2e6')};
  width: 100%;
  background-color: #fff;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => (props.$hasError ? '#fa5252' : '#4263eb')};
    box-shadow: ${props => (props.$hasError 
      ? '0 0 0 3px rgba(250, 82, 82, 0.2)' 
      : '0 0 0 3px rgba(66, 99, 235, 0.2)')};
  }
  
  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const IconWrapper = styled.div<{ isLeft: boolean }>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  ${props => props.isLeft ? 'left: 12px;' : 'right: 12px;'}
  color: #868e96;
`;

const ErrorMessage = styled.p`
  font-size: 0.75rem;
  color: #fa5252;
  margin-top: 4px;
  margin-bottom: 0;
`;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, leftIcon, rightIcon, isFullWidth = true, ...props }, ref) => {
    return (
      <InputContainer $isFullWidth={isFullWidth}>
        {label && <Label>{label}</Label>}
        <InputWrapper>
          {leftIcon && <IconWrapper isLeft>{leftIcon}</IconWrapper>}
          <StyledInput
            ref={ref}
            $hasLeftIcon={!!leftIcon}
            $hasRightIcon={!!rightIcon}
            $hasError={!!error}
            {...props}
          />
          {rightIcon && <IconWrapper isLeft={false}>{rightIcon}</IconWrapper>}
        </InputWrapper>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </InputContainer>
    );
  }
);